import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {MeterFlowVolumeEntity, Quantity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {useCallback, useMemo} from "react";
import {ActionIcon, Box, Group, Indicator, Modal, ModalProps, Stack} from "@mantine/core";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {HasAccess} from "../../auth/HasAccess/HasAccess"
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";
import {useMeterFlowVolumeDataTableFilter} from "./useMeterFlowVolumeDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {DataTableSumFooter} from "../DataTable/footer/DataTableSumFooter/DataTableSumFooter";
import {Tooltip} from "../../tooltip/Tooltip/Tooltip";
import {DataTableColumn} from "mantine-datatable/dist/types/DataTableColumn";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

const EMPTY_ARRAY: MeterFlowVolumeEntity[] = []

export type MeterFlowVolumeDataTableProps =
  EntityDataTableProps<MeterFlowVolumeEntity>
  & {
  onEdit?: (record: MeterFlowVolumeEntity) => void
  onDelete?: (record: MeterFlowVolumeEntity) => void
}

export const MeterFlowVolumeDataTable = (props: MeterFlowVolumeDataTableProps) => {
  const {
    records: meterFlowVolumes = EMPTY_ARRAY,
    recordsPerPageOptions,
    onEdit,
    onDelete,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useMeterFlowVolumeDataTableFilter({
    records: meterFlowVolumes
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'dttm', direction: 'asc'},
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const onEditClick = useCallback((record: MeterFlowVolumeEntity) => {
    if (onEdit) {
      onEdit(record)
    }
  }, [onEdit])

  const onDeleteClick = useCallback((record: MeterFlowVolumeEntity) => {
    if (onDelete) {
      onDelete(record)
    }
  }, [onDelete])

  const columns = useMemo(() => {
    const cols: DataTableColumn<MeterFlowVolumeEntity>[] = [
      {
        title: t("table.entity.meterflowvolume.column.date", "Date"),
        accessor: "dttm",
        width: 100,
        sortable: true,
        render: ({dttm}) => {
          return <FormattedDateTime date={dttm} displayTime={false}/>
        },
      },
      {
        title: t("table.entity.meterflowvolume.column.timegroup", "Time Group"),
        accessor: "timeGroup",
        width: 100,
        sortable: true,
        render: ({timeGroup}) => (
          t(timeGroup.displayValue.id, timeGroup.displayValue.defaultMessage)
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "meter"),
        title: t("table.entity.meterflowvolume.column.meter", "Meter"),
        accessor: "objectName",
        width: 100,
        sortable: true,
        render: ({meterId, meterName,}) => (
          <EntityAnchor
            value={{
              objectId: meterId,
              objectName: meterName,
              objectType: "METER"
            }}
          />
        ),
      },
      {
        title: t("table.entity.meterflowvolume.column.quantity", "Qty"),
        accessor: "quantity",
        width: 100,
        sortable: true,
        render: ({quantity, unitOfMeasure, quantityOverride, quantityOverrideComment, actualQuantity}) => {
          const qty: Partial<Quantity> = {
            number: quantity,
            units: unitOfMeasure
          }

          if (quantityOverride) {
            return (
              <Tooltip
                label={
                  <Stack spacing="xs">
                    <Group spacing="xs">
                      {t("common.entity.meterflowvolume.actualquantity", "Actual") + ":"}
                      <FormattedQuantity
                        value={{
                          number: actualQuantity,
                          units: qty.units,
                        }}
                      />
                    </Group>
                    {quantityOverrideComment && quantityOverrideComment}
                  </Stack>
                }>
                <Indicator inline size={6} color="orange">
                  <FormattedQuantity
                    value={qty}
                  />
                </Indicator>
              </Tooltip>
            )
          } else {
            return <FormattedQuantity value={qty}/>
          }
        },
        footer: (
          <DataTableSumFooter
            value={{
              number: filteredRecords.reduce((prev, curr) => prev + curr.quantity, 0)
            }}
          />
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "freightType"),
        title: t("table.entity.meterflowvolume.column.freighttype", "Freight Type"),
        accessor: "freightTypeName",
        width: 100,
        sortable: true,
        render: ({freightTypeName}) => (
          <DefaultText value={freightTypeName}/>
        )
      },
    ]

    if (onEdit || onDelete) {
      cols.push({
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: (meterFlowVolume) => (
          <HasAccess permissions={["edit"]}>
            <Group spacing={4} position="right" noWrap>
              {onEdit &&
                <ActionIcon onClick={() => onEditClick(meterFlowVolume)}>
                  <IconEdit size={16}/>
                </ActionIcon>
              }
              {onDelete &&
                <ActionIcon color="red" onClick={() => onDeleteClick(meterFlowVolume)}>
                  <IconTrash size={16}/>
                </ActionIcon>
              }
            </Group>
          </HasAccess>
        )
      })
    }

    return cols
  }, [onEdit, onDelete])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  );
};

export type MeterFlowVolumeDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: MeterFlowVolumeDataTableProps
}

export const MeterFlowVolumeDataTableModal = (props: MeterFlowVolumeDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.meterflowvolume.plural", "Meter Flow Volumes"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <MeterFlowVolumeDataTable
          {...dataTable} />
      </Box>
    </Modal>
  );
};
